import { getFunctions, httpsCallable } from 'firebase/functions';

const USER_KEY_CACHE = 'user_key';

interface UserKeyCache {
  key: string;
  userId: string;
  cachedAt: number;
}

/**
 * Get cached user encryption key from localStorage
 * @param userId - Current user ID to validate cache belongs to this user
 * @returns Cached encryption key or null if not found/invalid
 */
export const getCachedUserKey = (userId: string): string | null => {
  try {
    const cached = localStorage.getItem(USER_KEY_CACHE);
    if (!cached) return null;

    const parsedCache: UserKeyCache = JSON.parse(cached);

    // Validate userId matches (prevent cache leakage between users)
    if (parsedCache.userId !== userId) {
      clearUserKeyCache();
      return null;
    }

    return parsedCache.key;
  } catch (error) {
    console.error('Error reading cached key:', error);
    return null;
  }
};

/**
 * Cache user encryption key in localStorage
 * @param userId - Current user ID
 * @param key - Encryption key from server
 */
export const cacheUserKey = (userId: string, key: string): void => {
  try {
    const cache: UserKeyCache = {
      key,
      userId,
      cachedAt: Date.now()
    };
    localStorage.setItem(USER_KEY_CACHE, JSON.stringify(cache));
  } catch (error) {
    console.error('Error caching key:', error);
  }
};

/**
 * Clear user key cache
 * MUST be called on logout to prevent cache leakage
 */
export const clearUserKeyCache = (): void => {
  try {
    localStorage.removeItem(USER_KEY_CACHE);
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};

/**
 * Fetch user encryption key from Cloud Function
 * Rate limited to 10 requests per user per hour
 * @throws Error with user-friendly message if request fails
 */
const fetchUserKeyFromAPI = async (): Promise<string> => {
  const functions = getFunctions();
  const getUserKeyFn = httpsCallable(functions, 'generateUserKey');

  try {
    const result = await getUserKeyFn();
    const data = result.data as { subscriptionId: string };
    return data.subscriptionId;
  } catch (error: any) {
    // Provide user-friendly error messages
    if (error.code === 'resource-exhausted') {
      throw new Error('Rate limit exceeded. Please try again in an hour.');
    } else if (error.code === 'not-found') {
      throw new Error('No active subscription found. Please check your subscription status.');
    } else if (error.code === 'unauthenticated') {
      throw new Error('You must be logged in to access this feature.');
    }

    console.error('Error fetching key:', error);
    throw new Error('Failed to fetch encryption key. Please try again.');
  }
};

// Deduplicate concurrent fetches: generateUserKey is rate limited to 10/hour per user,
// so parallel callers must share one in-flight request instead of each burning quota.
let pendingFetch: { userId: string; promise: Promise<string> } | null = null;

/**
 * Get user encryption key (from cache or fetch from API)
 * This is the main function to retrieve the encryption key
 *
 * Flow:
 * 1. Check localStorage cache first (instant - 99% of requests)
 * 2. If cache miss, fetch from Cloud Function (2-3 seconds)
 * 3. Cache the result for future use
 *
 * @param userId - Current user ID
 * @returns Encryption key for this user
 * @throws Error if unable to fetch encryption key
 */
export const getUserKey = async (userId: string): Promise<string> => {
  // Try cache first (99% of requests will hit cache)
  const cached = getCachedUserKey(userId);
  if (cached) {
    return cached;
  }

  if (pendingFetch && pendingFetch.userId === userId) {
    return pendingFetch.promise;
  }

  // Cache miss - fetch from Cloud Function
  const promise = fetchUserKeyFromAPI()
    .then((key) => {
      cacheUserKey(userId, key);
      return key;
    })
    .finally(() => {
      pendingFetch = null;
    });

  pendingFetch = { userId, promise };
  return promise;
};
